import { generatePath } from 'react-router-dom';

import ROUTES from '@/shared/constants/routes';

export const MY_INVOICES_SECTION_ID = 'mis-facturas';

/**
 * El mes viaja en la ruta (`/facturacion/mis-facturas/2026-08/<uuid>`): el
 * listado se pagina por meses, y sin él un detalle abierto sin caché tendría
 * que recorrer el historial para encontrar la factura.
 */
export const RECEIVED_INVOICE_PARAM = 'tipo';

export const myInvoicePath = (invoice: { uuid: string; period: string; kind?: string }) => {
  const path = generatePath(ROUTES.FACTURACION_MY_INVOICE, { periodo: invoice.period, uuid: invoice.uuid });
  return invoice.kind === 'recibida' ? `${path}?${RECEIVED_INVOICE_PARAM}=recibida` : path;
};

/** El `location.state` con el que se llega al home pidiendo aterrizar en «Mis facturas». */
export type MyInvoicesLanding = { scrollToMyInvoices?: boolean };

export const MY_INVOICES_LANDING: MyInvoicesLanding = { scrollToMyInvoices: true };

export const wantsMyInvoices = (state: unknown): boolean =>
  (state as MyInvoicesLanding | null)?.scrollToMyInvoices === true;

/** Desde qué pantalla se pidió el listado: solo el tipo de vista, nunca el folio ni el id. */
export type MyInvoicesFrom = 'home' | 'qr' | 'stamped' | 'other';

export const myInvoicesFromOf = (pathname: string): MyInvoicesFrom => {
  if (pathname === ROUTES.FACTURACION) return 'home';
  if (pathname.startsWith(ROUTES.FACTURACION_QR)) return 'qr';
  if (pathname.startsWith('/facturacion/f/')) return 'stamped';
  return 'other';
};

/**
 * Lleva el listado al borde de arriba. El scroll lo lleva el `<main>` del
 * layout, no la ventana, y `scrollIntoView` mueve al contenedor que haga falta.
 * Con movimiento reducido llega de golpe.
 */
export const scrollToMyInvoices = (behavior: ScrollBehavior = 'smooth'): boolean => {
  const section = document.getElementById(MY_INVOICES_SECTION_ID);
  if (!section) return false;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  section.scrollIntoView({ behavior: reduceMotion ? 'auto' : behavior, block: 'start' });
  return true;
};

/**
 * Cómo estaba el listado al abrir una factura. Solo las emitidas tienen detalle, así
 * que se vuelve a esa pestaña. El scroll del `<main>` no se restaura solo, así que al
 * volver con «atrás» a esa misma entrada del historial (`homeKey`) el home se remonta
 * con la misma búsqueda, los mismos meses abiertos y la factura abierta en pantalla.
 * El detalle también lo lee para pasar de una factura a la siguiente en ese orden.
 */
export interface MyInvoicesListView {
  homeKey: string;
  query: string;
  openMonths: string[];
  focus: string;
  /** El `?periodo=` de la lista, si tenía: el detalle recorre las facturas de ese periodo. */
  period?: string | null;
}

let lastListView: MyInvoicesListView | null = null;

export const rememberListView = (view: MyInvoicesListView) => {
  lastListView = view;
};

export const lastMyInvoicesListView = () => lastListView;

export const listViewToRestore = (homeKey: string, isPop: boolean) =>
  isPop && lastListView?.homeKey === homeKey ? lastListView : null;

export const MY_INVOICE_ROW_ATTRIBUTE = 'data-my-invoice';

export const scrollToMyInvoiceRow = (uuid: string): boolean => {
  const row = document.querySelector(`[${MY_INVOICE_ROW_ATTRIBUTE}="${CSS.escape(uuid)}"]`);
  if (!row) return false;
  row.scrollIntoView({ behavior: 'auto', block: 'center' });
  return true;
};
